import {
    AfterViewInit,
    Component,
    ElementRef,
    inject,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {GoogleMap, GoogleMapsModule} from '@angular/google-maps';
import {MapService} from '../../services/map.service';
import {CommonModule, NgIf} from '@angular/common';
import {ErrorService} from '../../services/error.service';
import {PreviewMapStyle} from '../../../config/map';
import {SpotDetailModalComponent} from '../spot-detail-modal/spot-detail-modal.component';
import {Information} from '../../../interfaces/spot.interface';
import {ModalConfig} from '../../../interfaces/modal';
import {SpotsService} from '../../../services/spots.service';
import {AuthService} from '../../services/auth.service';
import {finalize, take} from 'rxjs';
import {SharedService} from '../../services/shared.service';
import {environment} from '../../../../environments/environment';

@Component({
    selector: 'app-map-preview',
    templateUrl: './map-preview.component.html',
    styleUrls: ['./map-preview.component.scss'],
    imports: [
        GoogleMap,
        GoogleMapsModule,
        NgIf,
        CommonModule,
        SpotDetailModalComponent,
    ]
})
export class MapPreviewComponent implements OnInit, AfterViewInit {
    @ViewChild('gMap') gMap!: GoogleMap;
    @ViewChild('markerContainer') markerContainerRef!: ElementRef;
    @ViewChild('spotPin', {static: false}) spotPinTemplate!: TemplateRef<any>;
    @ViewChild('spotPin', {read: ViewContainerRef, static: false}) viewContainerRef!: ViewContainerRef;
    map!: any;

    center = {lat: 35.184675602212145, lng: 136.899677906582};
    zoom = 14;
    apiLoaded = false;
    mapId = environment.googleMapId;
    mapStyle: google.maps.MapTypeStyle[] = PreviewMapStyle;
    mapOptions: google.maps.MapOptions = {
        styles: this.mapStyle,
        disableDefaultUI: true,
    }

    mapService = inject(MapService);
    errorService = inject(ErrorService);
    spotService = inject(SpotsService);
    authService = inject(AuthService);
    sharedService = inject(SharedService);

    selectSpot!: Information;

    spots: Information[] = [];

    spotDetailModalConfig: ModalConfig = {
        isOpen: false
    }

    ngOnInit() {
        this.mapService.load().then(() => {
            return google.maps.importLibrary('maps');
        }).then(() => {
            this.apiLoaded = true;
        }).catch((err) => {
            this.errorService.onError(err);
        });
    }

    ngAfterViewInit() {
        this.authService.getUserProjectName().then(async projectName => {
            const loading = await this.sharedService.presentLoading();
            await loading.present();
            (await this.spotService.getItemList(projectName)).pipe(take(1), finalize(() => loading.dismiss())).subscribe(async data => {
                data.items.filter((spot: any) => spot.information.public !== 'PRIVATE').forEach((item: any) => {
                    item.information.id = item.itemId;
                    if (item.information?.location?.length !== 2) return;
                    let lat = parseFloat(item.information?.location[0] as any);
                    let lng = parseFloat(item.information?.location[1] as any);
                    if (isNaN(lat) || isNaN(lng)) {
                        lat = 0;
                        lng = 0;
                    }
                    if (lat > lng) {
                        [lat, lng] = [lng, lat];
                    }
                    item.information.location[0] = lat;
                    item.information.location[1] = lng;
                    this.spots.push(item.information);
                });
                await this.initMarkers();
            })
        }).catch(error => this.errorService.onError(error));
    }

    clickSpotPin(spot: Information) {
        this.selectSpot = spot;
        this.spotDetailModalConfig.isOpen = true;
    }

    async initMarkers() {
        if (!this.gMap.googleMap) {
            console.error("Google Map is not initialized.");
            return;
        }

        const {AdvancedMarkerElement} = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

        this.spots.forEach((spot) => {
            const content = this.createMarkerContent(spot);
            const marker = new AdvancedMarkerElement({
                map: this.gMap.googleMap,
                position: {lat: spot.location[0], lng: spot.location[1]},
                content
            });

            content.addEventListener('click', () => this.clickSpotPin(spot));
        });
    }

    createMarkerContent(spot: any): HTMLElement {
        const embeddedView = this.viewContainerRef.createEmbeddedView(this.spotPinTemplate, {spot});
        embeddedView.detectChanges();

        const div = document.createElement('div');
        embeddedView.rootNodes.forEach(node => div.appendChild(node));

        return div;
    }
}
